import { apiSlice } from '../apiSlice';
import { SuccessResponse } from '../DTO/common.interface';
import {
	LoginAccountCheckDTO,
	LoginAccountDTO,
	LoginAccountTarget,
	LoginIdCheckDTO,
	ProgramAppSettingsRequest,
} from '../DTO/login-account/loginAccount.interface';

const baseUrl = ({ entity, id }: LoginAccountTarget) =>
	`/web/login-account/${entity}/${id}`;

export const loginAccountApi = apiSlice.injectEndpoints({
	endpoints: (builder) => ({
		getLoginAccount: builder.query<
			SuccessResponse<LoginAccountDTO>,
			LoginAccountTarget
		>({
			query: (target) => ({
				url: baseUrl(target),
				method: 'GET',
			}),
			providesTags: ['LoginAccount'],
		}),
		getLoginAccountCheck: builder.query<
			SuccessResponse<LoginAccountCheckDTO>,
			LoginAccountTarget
		>({
			query: (target) => ({
				url: `${baseUrl(target)}/check`,
				method: 'GET',
			}),
			providesTags: ['LoginAccountCheck'],
		}),
		createLoginAccount: builder.mutation<
			SuccessResponse<LoginAccountDTO>,
			LoginAccountTarget
		>({
			query: (target) => ({
				url: baseUrl(target),
				method: 'POST',
			}),
			invalidatesTags: ['LoginAccount', 'LoginAccountCheck'],
		}),
		checkLoginId: builder.mutation<
			SuccessResponse<LoginIdCheckDTO>,
			LoginAccountTarget & { loginId: string }
		>({
			query: ({ loginId, ...target }) => ({
				url: `${baseUrl(target)}/login-id/check`,
				method: 'POST',
				body: { loginId },
			}),
		}),
		updateLoginId: builder.mutation<
			SuccessResponse<LoginAccountDTO>,
			LoginAccountTarget & { loginId: string }
		>({
			query: ({ loginId, ...target }) => ({
				url: `${baseUrl(target)}/login-id`,
				method: 'PUT',
				body: { loginId },
			}),
			invalidatesTags: ['LoginAccount', 'LoginAccountCheck'],
		}),
		resetLoginPassword: builder.mutation<
			SuccessResponse<LoginAccountDTO>,
			LoginAccountTarget
		>({
			query: (target) => ({
				url: `${baseUrl(target)}/password/reset`,
				method: 'PUT',
			}),
			invalidatesTags: ['LoginAccount'],
		}),
		setLoginPassword: builder.mutation<
			SuccessResponse<LoginAccountDTO>,
			LoginAccountTarget & { password: string }
		>({
			query: ({ password, ...target }) => ({
				url: `${baseUrl(target)}/password`,
				method: 'PUT',
				body: { password },
			}),
			invalidatesTags: ['LoginAccount'],
		}),
		// Program App settings live on the login account (member and family member)
		updateProgramAppSettings: builder.mutation<
			SuccessResponse<LoginAccountDTO>,
			LoginAccountTarget & ProgramAppSettingsRequest
		>({
			query: ({ entity, id, ...body }) => ({
				url: baseUrl({ entity, id }),
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['LoginAccount', 'FamilyDetail'],
		}),
	}),
});

export const {
	useGetLoginAccountQuery,
	useLazyGetLoginAccountQuery,
	useGetLoginAccountCheckQuery,
	useCreateLoginAccountMutation,
	useCheckLoginIdMutation,
	useUpdateLoginIdMutation,
	useResetLoginPasswordMutation,
	useSetLoginPasswordMutation,
	useUpdateProgramAppSettingsMutation,
} = loginAccountApi;
